/**
 * Pure data helpers for the Clicks trend chart: the ratios on each point and
 * the compare overlay (which selected rows are drawn, and how each is scoped).
 * No React, so they unit-test on their own.
 */
import { filterKeyForGroupBy } from "@/config/dimensions";
import type { ClicksTrendPoint } from "@/services/ClicksService";

/** Selected rows drawn on the trend chart; the rest stay selected for the table actions. */
export const COMPARE_MAX = 5;

/** Compare: colour = entity (one per drawn row), dash = metric. */
export const COMPARE_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ec4899", "#06b6d4"];

const num = (v: unknown): number =>
  typeof v === "number" ? v : v == null ? 0 : Number(v) || 0;
const pct = (a: number, b: number): number => (b > 0 ? (a / b) * 100 : 0);
const rate = (a: number, b: number): number => (b > 0 ? a / b : 0);

/**
 * Keitaro totals + the ratios the table derives (same formulas as
 * get_dynamic_stats). Base fields coerced to numbers (PostgREST sends numeric
 * as strings). Shared by the normal view and the compare overlay.
 *
 * CR % divides by unique visitors, as the table has since migration
 * 20261008100000 (bots click ~12 times each, so a clicks base deflated every
 * CR several-fold); no visitors = 0, as the table shows it.
 */
export const enrichPoint = (p: ClicksTrendPoint): Record<string, number> => {
  const clicks = num(p.total_clicks);
  const uniq = num(p.unique_global);
  const rev = num(p.total_revenue);
  return {
    total_clicks: clicks,
    unique_global: uniq,
    total_conversions: num(p.total_conversions),
    total_leads: num(p.total_leads),
    total_sales: num(p.total_sales),
    total_bots: num(p.total_bots),
    total_revenue: rev,
    epc: rate(rev, clicks),
    uepc: rate(rev, uniq),
    ecpm: rate(rev, clicks) * 1000,
    cr_percent: pct(num(p.total_conversions), uniq),
    cr_leads_percent: pct(num(p.total_leads), uniq),
    cr_sales_percent: pct(num(p.total_sales), uniq),
    bot_share_percent: pct(num(p.total_bots), clicks),
  };
};

/**
 * The filter overlay that scopes a trend to one selected row: an include
 * filter per grouping dimension. A composite row ("GB ▸ Drip Casino" under
 * country ▸ brand) takes its parts from dimension_parts when the row has them,
 * and only otherwise from splitting the label.
 */
export const compareFiltersFor = (
  value: string,
  dims: string[],
  parts?: string[] | null,
): Record<string, { mode: "include"; values: string[] }> => {
  const split = parts?.length === dims.length ? parts : value.split(" ▸ ");
  return Object.fromEntries(
    dims.map((dim, i) => [
      filterKeyForGroupBy(dim),
      { mode: "include" as const, values: [split[i] ?? value] },
    ]),
  );
};
